import {Link} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {Tag} from '@/components/ui/tag'
import {cn} from '@/lib/utils'
import {CTA_BASE, CTA_SELLO_GRANDE} from './kit'

/** Datos de ejemplo, no copy, con la rampa de `components/sgsst/score-card.tsx`. */
const CICLOS = [
  {label: 'PLANEAR', fraccion: '17,5 / 25', pct: 70, fill: 'bg-chart-planear'},
  {label: 'HACER', fraccion: '30,5 / 60', pct: 51, fill: 'bg-chart-hacer'},
  {label: 'VERIFICAR', fraccion: '5 / 5', pct: 100, fill: 'bg-chart-verificar'},
  {label: 'ACTUAR', fraccion: '10 / 10', pct: 100, fill: 'bg-chart-actuar'},
] as const

function MicroDato({label, valor}: {label: string; valor: string}) {
  return (
    <div className="min-w-0">
      <p className="font-mono text-[10px] font-semibold tracking-[0.12em] text-muted-foreground uppercase">{label}</p>
      <p className="mt-1 font-mono text-[13px] text-foreground">{valor}</p>
    </div>
  )
}

/** En DOM y no como captura: una captura es ilegible en un teléfono y envejece con la pantalla real. */
function Muestra() {
  const {t} = useTranslation()
  return (
    <div className="relative">
      <div aria-hidden="true" className="absolute inset-x-8 -top-3 h-12 rounded-xl bg-card/8" />
      <div aria-hidden="true" className="absolute inset-x-4 -top-1.5 h-12 rounded-xl bg-card/15" />
      <div className="relative">
        <div className="surface overflow-hidden">
          <div className="surface-divider border-b px-5 py-4">
            <div className="flex items-start justify-between gap-3">
              <p className="font-mono text-[10px] font-semibold tracking-[0.12em] text-muted-foreground uppercase">
                {t('landing.hero.muestra.eyebrow')}
              </p>
              <Tag tone="neutral">{t('landing.hero.muestra.sello')}</Tag>
            </div>
            <p className="mt-1.5 font-mono text-[10px] font-semibold tracking-[0.12em] text-muted-foreground/80 uppercase">
              {t('landing.hero.muestra.estado')}
            </p>
            <div className="mt-1 flex flex-wrap items-center gap-3">
              <span className="font-heading text-3xl text-primary">{t('landing.hero.muestra.pct')}</span>
              <Tag tone="amber">{t('landing.hero.muestra.banda')}</Tag>
            </div>
            <p className="mt-2 text-xs text-muted-foreground">{t('landing.hero.muestra.resueltos')}</p>
            <div className="mt-4 flex flex-wrap gap-x-6 gap-y-3">
              <MicroDato
                label={t('landing.hero.muestra.regimenLabel')}
                valor={t('landing.hero.muestra.regimenValor')}
              />
              <MicroDato label={t('landing.hero.muestra.tablaLabel')} valor={t('landing.hero.muestra.tablaValor')} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-x-5 gap-y-4 px-5 py-4 sm:grid-cols-4 lg:grid-cols-2">
            {CICLOS.map((ciclo) => (
              <div key={ciclo.label} className="min-w-0">
                <div className="flex items-baseline justify-between gap-2">
                  <span className="truncate font-mono text-[10px] font-semibold tracking-[0.12em] text-foreground uppercase">
                    {ciclo.label}
                  </span>
                  <span className="shrink-0 font-mono text-[10px] text-muted-foreground">{ciclo.fraccion}</span>
                </div>
                <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-track">
                  <div className={cn('h-full rounded-full', ciclo.fill)} style={{width: `${ciclo.pct}%`}} />
                </div>
              </div>
            ))}
          </div>
        </div>
        <p className="mt-3 text-xs text-ink-deep-foreground/65">{t('landing.hero.muestra.pie')}</p>
      </div>
    </div>
  )
}

export function LandingHero() {
  const {t} = useTranslation()
  return (
    <section className="relative overflow-hidden bg-ink-deep text-ink-deep-foreground">
      <div aria-hidden="true" className="cover-ruled absolute inset-0" />
      <div className="relative mx-auto grid max-w-6xl gap-14 px-6 pt-16 pb-20 sm:pt-24 sm:pb-28 lg:grid-cols-[1.05fr_0.95fr] lg:items-center lg:gap-16">
        <div>
          <div className="flex items-center gap-3 text-ink-deep-foreground/70">
            <span className="relative inline-block h-px w-10 bg-hairline-on-ink">
              <span className="mark-travel absolute top-1/2 h-1.5 w-1.5 -translate-y-1/2 rounded-full bg-primary-foreground" />
            </span>
            <p className="font-mono text-[11px] font-semibold tracking-[0.14em] uppercase">
              {t('landing.hero.eyebrow')}
            </p>
          </div>
          <h1 className="mt-7 max-w-[19ch] font-heading text-[clamp(2.125rem,4.4vw,3.5rem)] leading-[1.05] tracking-tight text-ink-deep-foreground">
            {t('landing.hero.title')} <span className="text-ink-deep-foreground/70">{t('landing.hero.titleTail')}</span>
          </h1>
          <p className="mt-6 max-w-xl text-base text-ink-deep-foreground/75 sm:text-lg">{t('landing.hero.subtitle')}</p>
          <div className="mt-9 flex flex-col items-stretch gap-3 sm:flex-row sm:items-center">
            <Link
              to="/registro/empresa"
              className={cn(CTA_SELLO_GRANDE, 'w-full sm:w-auto')}
              data-testid="landing-hero-cta-primary">
              {t('landing.hero.ctaPrimary')}
            </Link>
            <Link
              to="/login"
              className={cn(
                CTA_BASE,
                'w-full border border-hairline-on-ink text-ink-deep-foreground hover:bg-white/5 sm:w-auto'
              )}
              data-testid="landing-hero-cta-secondary">
              {t('landing.hero.ctaSecondary')}
            </Link>
          </div>
          <p className="mt-4 font-mono text-[11px] text-ink-deep-foreground/55">{t('landing.precios.ctaAyuda')}</p>
        </div>
        <Muestra />
      </div>
    </section>
  )
}
